import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { analyticsApi } from '../services/api';
import { Users } from 'lucide-react';

export const AllianceToolPage: React.FC = () => {
  const [availableTeams, setAvailableTeams] = useState<number[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<number[]>([]);
  const [synergy, setSynergy] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await analyticsApi.getTeamRankings();
      setAvailableTeams(response.data.data.map((t: any) => t.team_number));
    } catch (err) {
      console.error('Failed to load teams');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTeam = (teamNumber: number) => {
    if (selectedTeams.includes(teamNumber)) {
      setSelectedTeams(selectedTeams.filter((t) => t !== teamNumber));
    } else if (selectedTeams.length < 3) {
      setSelectedTeams([...selectedTeams, teamNumber]);
    }
  };

  const calculateSynergy = async () => {
    if (selectedTeams.length === 3) {
      try {
        // Backend returns { status, alliance: { teams, synergy_score, ... } }
        const response = await analyticsApi.calculateAllianceSynergy(selectedTeams);
        setSynergy(response.data.data);
      } catch (err) {
        console.error('Error calculating synergy');
      }
    }
  };

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="glass p-6">
          <h1 className="section-title flex items-center gap-2">
            <Users className="w-6 h-6" />
            Alliance Builder
          </h1>
          <p className="text-gray-400">Select 3 teams to analyze alliance synergy</p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Selection */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 20 }} className="lg:col-span-2">
          <div className="glass p-6">
            <h2 className="subsection-title">Available Teams</h2>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {availableTeams.map((team) => (
                <motion.button
                  key={team}
                  onClick={() => toggleTeam(team)}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full p-3 rounded-lg transition-all text-left ${
                    selectedTeams.includes(team)
                      ? 'bg-blue-600/30 border border-blue-500/50'
                      : 'bg-gray-800/30 border border-gray-700/30 hover:bg-gray-800/50'
                  }`}
                >
                  Team {team} {selectedTeams.includes(team) && '✓'}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Selected & Synergy */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 20 }} transition={{ delay: 0.1 }}>
          <div className="glass p-6">
            <h2 className="subsection-title">Selected Alliance</h2>
            <div className="mb-6">
              {selectedTeams.map((team) => (
                <div key={team} className="p-2 bg-blue-600/20 rounded mb-2 flex justify-between items-center">
                  <span>Team {team}</span>
                  <button onClick={() => toggleTeam(team)} className="text-xs text-gray-400 hover:text-white">
                    ✕
                  </button>
                </div>
              ))}
              {selectedTeams.length < 3 && (
                <p className="text-xs text-gray-400">Select {3 - selectedTeams.length} more team(s)</p>
              )}
            </div>

            <motion.button
              onClick={calculateSynergy}
              disabled={selectedTeams.length !== 3}
              whileHover={{ scale: 1.02 }}
              className="btn btn-primary btn-lg w-full"
            >
              Analyze Synergy
            </motion.button>

            {synergy && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 pt-6 border-t border-gray-700">
                <div className="mb-4">
                  <p className="text-xs text-gray-400 mb-1">Synergy Score</p>
                  <p className="text-2xl font-bold text-blue-400">{synergy.synergy_score.toFixed(1)}</p>
                </div>
                <div className="mb-4">
                  <p className="text-xs text-gray-400 mb-1">Total Contribution</p>
                  <p className="text-lg font-bold">{synergy.estimated_total_contribution.toFixed(1)}</p>
                </div>
                {synergy.weakness_overlap.length > 0 && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
                    {synergy.weakness_overlap.map((w: string) => (
                      <p key={w}>⚠ {w}</p>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
